import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Input, Select } from '../ui';
import { api, ApiError } from '../../lib/api';
import type { Check, Finding } from '../../lib/types';

const sevTone = (s: Finding['severity']) =>
  (s === 'critical' ? 'red' : s === 'major' ? 'amber' : s === 'info' ? 'blue' : 'green') as
    'red' | 'amber' | 'blue' | 'green';

/** Ingredient-check panel shown under a formulation version (FR-5). Polls while queued/running. */
export function CheckPanel({
  orgId,
  pid,
  version,
  canVerify,
  canOverride,
  canAccept,
  checkType = 'ingredient',
}: {
  orgId: string;
  pid: string;
  version: number;
  canVerify: boolean;
  canOverride: boolean;
  canAccept: boolean;
  checkType?: 'ingredient' | 'supplement_facts';
}) {
  const { t } = useTranslation();
  const base = `/api/v1/orgs/${orgId}/products/${pid}`;
  const [checks, setChecks] = useState<Check[]>([]);
  const [check, setCheck] = useState<Check | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<'all' | 'open'>('all');

  const load = useCallback(async () => {
    const r = await api<{ checks: Check[] }>(
      `${base}/checks?type=${checkType}&formulationVersion=${version}`,
    );
    setChecks(r.checks);
    if (r.checks[0]) setCheck(await api<Check>(`${base}/checks/${r.checks[0].id}`));
    else setCheck(null);
  }, [base, checkType, version]);
  useEffect(() => {
    void load().catch((e: unknown) => setErr(String(e)));
  }, [load]);
  useEffect(() => {
    if (!check || (check.status !== 'queued' && check.status !== 'running')) return;
    const id = setTimeout(() => void load().catch(() => setCheck((c) => (c ? { ...c } : c))), 3000); // re-arm on error
    return () => clearTimeout(id);
  }, [check, load]);

  const fail = (e: unknown) =>
    setErr(
      e instanceof ApiError
        ? t([`check.err_${e.code}`, 'check.err_generic'], { code: e.code }) + ` ${e.message}`
        : String(e),
    );
  const verify = async () => {
    setBusy(true);
    setErr(null);
    try {
      const url =
        checkType === 'supplement_facts' ? `${base}/sfp/verify` : `${base}/formulations/${version}/verify`;
      await api(url, { method: 'POST', json: { formulationVersion: version } });
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const dispose = async (item: Finding, disposition: Finding['disposition']) => {
    if (!check) return;
    setErr(null);
    try {
      const r = await api<{ result: Check['result'] }>(`${base}/checks/${check.id}/items/${item.id}`, {
        method: 'PATCH',
        json: { disposition, note: note[item.id] ?? null },
      });
      setCheck({ ...check, result: r.result });
    } catch (e) {
      fail(e);
    }
  };
  const running = check && (check.status === 'queued' || check.status === 'running');
  const items = (check?.result?.items ?? []).filter(
    (i) => filter === 'all' || (i.disposition === 'open' && i.severity !== 'ok'),
  );
  const order = { critical: 0, major: 1, info: 2, ok: 3 };
  items.sort((a, b) => order[a.severity] - order[b.severity]);
  return (
    <Card
      title={`${t('check.latest')} - v${version}`}
      actions={
        <>
          {check?.report && (
            <a
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
              href={`${base}/checks/${check.id}/report?download=1`}
            >
              {check.result?.status === 'pass' ? t('check.report') : t('check.fixReport')}
            </a>
          )}
          {canVerify && (
            <Button onClick={() => void verify()} disabled={busy || !!running}>
              {running ? t('check.verifying') : check ? t('check.retry') : t('check.verify')}
            </Button>
          )}
        </>
      }
    >
      {err && <Alert>{err}</Alert>}
      {!check && <p className="text-sm text-slate-500">{t('check.noChecks')}</p>}
      {check && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono text-xs text-slate-500">{check.id}</span>
            <Badge
              tone={
                check.status === 'done'
                  ? check.result?.status === 'pass'
                    ? 'green'
                    : 'red'
                  : check.status === 'failed'
                    ? 'red'
                    : 'blue'
              }
            >
              {check.status === 'done' && check.result
                ? t(`check.${check.result.status}`)
                : t(`check.${check.status}`)}
            </Badge>
            {check.result && (
              <span className="text-slate-600">
                {check.result.counts.critical ?? 0} {t('check.sev_critical')} ·{' '}
                {check.result.counts.major ?? 0} {t('check.sev_major')} · {check.result.counts.info ?? 0}{' '}
                {t('check.sev_info')} · {check.result.counts.ok ?? 0} {t('check.sev_ok')}
                {check.result.counts.overridden
                  ? ` · ${check.result.counts.overridden} ${t('check.overridden').toLowerCase()}`
                  : ''}
              </span>
            )}
            <span className="ml-auto text-xs text-slate-500">
              {t('check.kb')} {check.kbVersion} · {t('check.prompt')}{' '}
              {check.promptVersion?.slice(0, 8) ?? '-'}
              {check.metrics?.totalMs
                ? ` · ${t('check.took', { s: Math.round(check.metrics.totalMs / 1000) })}`
                : ''}
            </span>
          </div>
          {check.status === 'failed' && <Alert>{t('check.error', { msg: check.error })}</Alert>}
          {running && (
            <div className="h-2 w-full overflow-hidden rounded bg-slate-200">
              <div className="h-2 w-1/3 animate-pulse rounded bg-blue-500" />
            </div>
          )}
          {check.result && (
            <>
              <div className="flex gap-2 text-xs">
                <Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as 'all' | 'open')}
                  className="w-auto"
                >
                  <option value="all">
                    {t('check.items')}: all ({check.result.items.length})
                  </option>
                  <option value="open">
                    {t('check.open')} (
                    {check.result.items.filter((i) => i.disposition === 'open' && i.severity !== 'ok').length}
                    )
                  </option>
                </Select>
              </div>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2">{t('check.severity')}</th>
                    <th>{t('check.subject')}</th>
                    <th>{t('check.finding')}</th>
                    <th>{t('check.fix')}</th>
                    <th>{t('check.citation')}</th>
                    <th>{t('check.disposition')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 align-top">
                  {items.map((i) => (
                    <tr key={i.id} className={i.disposition === 'overridden' ? 'opacity-60' : ''}>
                      <td className="py-2">
                        <Badge tone={i.disposition === 'overridden' ? 'slate' : sevTone(i.severity)}>
                          {t(`check.sev_${i.severity}`)}
                        </Badge>
                        <div className="mt-1 text-xs text-slate-400">
                          {t(`check.source_${i.source}`)} · {Math.round(i.confidence * 100)}%
                        </div>
                      </td>
                      <td className="font-medium">{i.subject}</td>
                      <td className="max-w-xs">{i.finding}</td>
                      <td className="max-w-xs">
                        {i.fix.what}
                        {i.fix.suggestedText && (
                          <div className="mt-1 rounded bg-slate-100 px-2 py-1 font-mono text-xs">
                            {i.fix.suggestedText}
                          </div>
                        )}
                      </td>
                      <td className="text-xs text-slate-600">
                        {i.citation.url ? (
                          <a
                            className="text-emerald-700 underline"
                            href={i.citation.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {i.citation.section || i.citation.kbFile}
                          </a>
                        ) : (
                          i.citation.section || i.citation.kbFile
                        )}
                        <div className="text-slate-400">{i.citation.kbFile}</div>
                      </td>
                      <td className="text-xs">
                        <Badge
                          tone={
                            i.disposition === 'open'
                              ? 'slate'
                              : i.disposition === 'accepted'
                                ? 'blue'
                                : 'green'
                          }
                        >
                          {t(`check.${i.disposition}`)}
                        </Badge>
                        {i.humanNote && <div className="mt-1 text-slate-500">{i.humanNote}</div>}
                        {i.severity !== 'ok' && (canOverride || canAccept) && (
                          <div className="mt-1 space-y-1">
                            <Input
                              placeholder={t('check.noteLabel')}
                              value={note[i.id] ?? ''}
                              onChange={(e) => setNote({ ...note, [i.id]: e.target.value })}
                              className="text-xs"
                            />
                            <div className="flex gap-1">
                              {i.disposition !== 'accepted' && (
                                <Button
                                  variant="secondary"
                                  className="text-xs"
                                  onClick={() => void dispose(i, 'accepted')}
                                >
                                  {t('check.accept')}
                                </Button>
                              )}
                              {canOverride && i.disposition !== 'overridden' && (
                                <Button
                                  variant="secondary"
                                  className="text-xs"
                                  onClick={() => void dispose(i, 'overridden')}
                                >
                                  {t('check.override')}
                                </Button>
                              )}
                              {i.disposition !== 'open' && (
                                <Button
                                  variant="ghost"
                                  className="text-xs"
                                  onClick={() => void dispose(i, 'open')}
                                >
                                  {t('check.reopen')}
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {check.result.aiNotes && <p className="text-xs text-slate-500">AI: {check.result.aiNotes}</p>}
            </>
          )}
          {checks.length > 1 && (
            <details className="text-xs text-slate-500">
              <summary>
                {t('check.history')} ({checks.length})
              </summary>
              <ul className="mt-1 space-y-0.5">
                {checks.map((c) => (
                  <li key={c.id}>
                    <button
                      className="font-mono underline"
                      onClick={() => void api<Check>(`${base}/checks/${c.id}`).then(setCheck)}
                    >
                      {c.id}
                    </button>{' '}
                    {c.status} {c.result ? `· ${c.result.status}` : ''} ·{' '}
                    {c.createdAt.slice(0, 16).replace('T', ' ')} · {c.requestedByEmail}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}
